import { postConsentLog } from '../api/config';
import { configCache } from '../cache/memory';
import { pushConsentDefault, pushConsentUpdate, scriptsNeedGoogleConsentMode } from '../consent/gtag';
import { closeModal, isBarStyle, openModal, renderEntry, removeEntry, usesInlineBarActions } from '../consent/ui';
import {
  emitConsentChange,
  emitDebug,
  offConsentChange,
  offDebug,
  onConsentChange,
  onDebug,
} from '../debug';
import { removeIneligibleScripts } from '../scripts/inject';
import { clearConsent, readValidConsent, writeConsent } from '../storage/consent';
import type {
  ConsentAction,
  ConsentCategory,
  ConsentState,
  OrbitConsentChange,
  OrbitDebugEvent,
  RuntimeConfig,
} from '../types';

export type OrbitSdk = {
  openConsent: () => void;
  acceptConsent: () => void;
  rejectConsent: () => void;
  setConsent: (categories: Record<string, boolean>) => void;
  /** Withdraw one permission; scripts that needed it are removed or the page reloads. */
  revokeConsent: (category: string) => void;
  clearConsent: () => void;
  getConsent: () => ConsentState | null;
  hasConsent: (category?: string) => boolean;
  categories: () => ConsentCategory[];
  on: {
    (event: 'change', listener: (change: OrbitConsentChange) => void): () => void;
    (event: 'debug', listener: (event: OrbitDebugEvent) => void): () => void;
  };
  off: (event: 'change' | 'debug', listener: (payload: never) => void) => void;
  reload: () => Promise<void>;
  version: () => number | null;
  isPreview: () => boolean;
};

type RuntimeHooks = {
  siteKey: string;
  load: () => Promise<void>;
  apply: (config: RuntimeConfig, consent: ConsentState | null) => Promise<void>;
};

let hooks: RuntimeHooks | null = null;

export function bindRuntime(next: RuntimeHooks): OrbitSdk {
  hooks = next;
  return createSdk();
}

function createSdk(): OrbitSdk {
  const on = ((event: 'change' | 'debug', listener: (payload: never) => void) =>
    event === 'debug'
      ? onDebug(listener as (e: OrbitDebugEvent) => void)
      : onConsentChange(listener as (c: OrbitConsentChange) => void)) as OrbitSdk['on'];

  return {
    openConsent() {
      const config = configCache.get();
      if (!config || !hooks) return;
      if (usesInlineBarActions(config.consent)) {
        openPreferencesOnly(config, currentConsent(config));
      } else {
        openModalWithHandlers(config, currentConsent(config));
      }
    },
    acceptConsent() {
      const config = configCache.get();
      if (!config) return;
      void persist(config, true, allCategories(config), 'accept_all');
      closeModal();
    },
    rejectConsent() {
      const config = configCache.get();
      if (!config) return;
      void persist(config, false, essentialCategories(config), 'essential_only');
      closeModal();
    },
    setConsent(categories: Record<string, boolean>) {
      const config = configCache.get();
      if (!config) return;
      const next = withRequired(config, categories);
      void persist(config, isFullyAccepted(config, next), next, 'update');
      closeModal();
    },
    revokeConsent(category: string) {
      const config = configCache.get();
      if (!config) return;
      const current = currentConsent(config);
      const next = withRequired(config, { ...(current?.categories || {}), [category]: false });
      void persist(config, false, next, 'update');
    },
    clearConsent() {
      if (!hooks) return;
      clearConsent(hooks.siteKey);
    },
    getConsent() {
      const config = configCache.get();
      return config ? currentConsent(config) : null;
    },
    hasConsent(category?: string) {
      const config = configCache.get();
      const consent = config ? currentConsent(config) : null;
      if (!consent) return false;
      if (!category) return consent.accepted;
      return Boolean(consent.categories[category]);
    },
    categories() {
      return configCache.get()?.consent.categories ?? [];
    },
    on,
    off(event, listener) {
      if (event === 'debug') {
        offDebug(listener as unknown as (e: OrbitDebugEvent) => void);
        return;
      }
      offConsentChange(listener as unknown as (c: OrbitConsentChange) => void);
    },
    async reload() {
      configCache.clear();
      await hooks?.load();
    },
    version() {
      return configCache.get()?.version ?? null;
    },
    isPreview() {
      return Boolean(configCache.get()?.preview);
    },
  };
}

export function mountConsentUi(config: RuntimeConfig, siteKey: string): ConsentState | null {
  const behavior = config.consent.behavior;
  const { consent: existing, invalid } = readValidConsent(siteKey, behavior);

  if (invalid) {
    emitDebug(
      'consent_expired',
      invalid === 'version'
        ? 'Stored consent predates the current consent version, asking again'
        : 'Stored consent expired, asking again',
      { reason: invalid },
    );
  }

  if (!config.consent.enabled) {
    const granted =
      existing ||
      ({
        accepted: true,
        categories: allCategories(config),
        updatedAt: new Date().toISOString(),
      } satisfies ConsentState);
    removeEntry();
    return granted;
  }

  // Always honor browser opt-out when consent is on (no editor toggle).
  const respectGpc = behavior.respect_gpc !== false;
  const googleConsentMode =
    behavior.google_consent_mode || scriptsNeedGoogleConsentMode(config.scripts);

  // Global Privacy Control is an explicit opt-out signal, so start denied.
  const gpc = respectGpc && navigator.globalPrivacyControl === true;
  if (gpc && !existing) {
    emitDebug('gpc_detected', 'Global Privacy Control is on, optional permissions start denied');
  }

  if (googleConsentMode) {
    pushConsentDefault(config.consent.categories, existing?.categories || {});
  }

  const inlineBar = usesInlineBarActions(config.consent);
  const barStyle = isBarStyle(config.consent.entry.style);

  function remountEntry(): void {
    renderEntry(config.consent, {
      onOpen: () => {
        if (inlineBar) {
          openPreferencesOnly(config, currentConsent(config));
        } else {
          openModalWithHandlers(config, currentConsent(config));
        }
      },
      onAcceptAll: () => {
        void persist(config, true, allCategories(config), 'accept_all');
      },
      onEssentialOnly: () => {
        void persist(config, false, essentialCategories(config), 'essential_only');
      },
      onPreferences: () => openPreferencesOnly(config, currentConsent(config)),
    });
  }

  // Bars hide after a choice, do not remount for returning visitors.
  if (!(barStyle && existing)) {
    remountEntry();
  }

  if (existing) {
    emitDebug('consent_loaded', 'Returning visitor, using stored consent', {
      categories: existing.categories,
    });
    return existing;
  }

  if (gpc) {
    // Respect the signal without recording a choice the visitor never made.
    return { accepted: false, categories: essentialCategories(config), updatedAt: new Date().toISOString() };
  }

  // Inline bar is the first-visit UI, do not also pop the modal.
  if (behavior.auto_open && !inlineBar) {
    openModalWithHandlers(config, null);
  }

  emitDebug('consent_required', 'No stored consent, gated integrations are on hold');

  // Essential-safe scripts still inject; return null so gated scripts wait.
  return null;
}

function currentConsent(config: RuntimeConfig): ConsentState | null {
  if (!hooks) return null;

  return readValidConsent(hooks.siteKey, config.consent.behavior).consent;
}

function openModalWithHandlers(config: RuntimeConfig, current: ConsentState | null): void {
  // Blocking mode only traps first-time visitors; a saved choice can be reopened freely.
  const dismissible = !config.consent.behavior.blocking || Boolean(current);

  openModal(
    config.consent,
    current,
    {
      onAcceptAll: () => void persist(config, true, allCategories(config), 'accept_all'),
      onEssentialOnly: () => void persist(config, false, essentialCategories(config), 'essential_only'),
      onSavePreferences: (categories) => {
        const next = withRequired(config, categories);
        void persist(config, isFullyAccepted(config, next), next, 'update');
      },
    },
    { dismissible },
  );
}

function openPreferencesOnly(config: RuntimeConfig, current: ConsentState | null): void {
  const dismissible = !config.consent.behavior.blocking || Boolean(current);

  openModal(
    config.consent,
    current,
    {
      onAcceptAll: () => void persist(config, true, allCategories(config), 'accept_all'),
      onEssentialOnly: () => void persist(config, false, essentialCategories(config), 'essential_only'),
      onSavePreferences: (categories) => {
        const next = withRequired(config, categories);
        void persist(config, isFullyAccepted(config, next), next, 'update');
      },
    },
    { dismissible, view: 'preferences', preferencesOnly: true },
  );
}

async function persist(
  config: RuntimeConfig,
  accepted: boolean,
  categories: Record<string, boolean>,
  action: ConsentAction,
): Promise<void> {
  if (!hooks) return;

  const behavior = config.consent.behavior;
  const previous = currentConsent(config);
  const revoked = Object.keys(previous?.categories || {}).filter(
    (id) => previous?.categories[id] && !categories[id],
  );

  const state = writeConsent(
    hooks.siteKey,
    { accepted, categories, updatedAt: new Date().toISOString(), version: behavior.consent_version },
    behavior,
  );

  postConsentLog(hooks.siteKey, {
    visitor_key: hooks.siteKey + ':' + (readVisitorId() || createVisitorId()),
    action,
    categories,
  });

  if (behavior.google_consent_mode || scriptsNeedGoogleConsentMode(config.scripts)) {
    pushConsentUpdate(config.consent.categories, categories);
  }

  emitDebug('consent_saved', describeChoice(action, revoked), { action, categories, revoked });
  emitConsentChange(state, action, revoked);

  if (isBarStyle(config.consent.entry.style)) {
    removeEntry();
  }

  if (revoked.length > 0 && behavior.reload_on_revoke) {
    emitDebug('page_reloading', 'Reloading so withdrawn integrations stop running');
    window.location.reload();
    return;
  }

  if (revoked.length > 0) {
    removeIneligibleScripts(config.scripts, state, config.consent.categories);
  }

  await hooks.apply(config, state);
}

function describeChoice(action: ConsentAction, revoked: string[]): string {
  if (revoked.length > 0) {
    return `Consent updated, withdrawn: ${revoked.join(', ')}`;
  }
  if (action === 'accept_all') return 'Visitor accepted all permissions';
  if (action === 'essential_only') return 'Visitor kept essential only';

  return 'Visitor saved custom preferences';
}

function isFullyAccepted(config: RuntimeConfig, categories: Record<string, boolean>): boolean {
  return config.consent.categories.every((c) => categories[c.id]);
}

function allCategories(config: RuntimeConfig): Record<string, boolean> {
  const categories: Record<string, boolean> = {};
  for (const category of config.consent.categories) {
    categories[category.id] = true;
  }
  return categories;
}

function essentialCategories(config: RuntimeConfig): Record<string, boolean> {
  const categories: Record<string, boolean> = {};
  for (const category of config.consent.categories) {
    categories[category.id] = Boolean(category.required);
  }
  return categories;
}

function withRequired(config: RuntimeConfig, categories: Record<string, boolean>): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  for (const category of config.consent.categories) {
    next[category.id] = category.required ? true : Boolean(categories[category.id]);
  }
  return next;
}

function readVisitorId(): string | null {
  try {
    return localStorage.getItem('orbit:vid');
  } catch {
    return null;
  }
}

function createVisitorId(): string {
  const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
  try {
    localStorage.setItem('orbit:vid', id);
  } catch {
    // Non-persistent visitor id is fine.
  }
  return id;
}

export { clearConsent };
